/**
 * Display mapping for `RewardTransaction.type`.
 *
 * The backend stores `type` as a free-form string — there is no enum on the
 * model — and the award paths disagree on spelling:
 *   - `rewards-account.js` writes spaces: 'PACKAGE SHIPPED'
 *   - `referral-rewards.js` writes underscores: 'EMAIL_VERIFICATION_REFERRER'
 *   - `routes.js` / `recalculatePoints` interpolate a user id: 'REFERRED BY 42'
 *
 * Matching on any single spelling silently drops the rest, so everything is
 * normalised (upper-cased, underscores folded to spaces, whitespace collapsed)
 * before lookup, and unknown types fall back to a title-cased label rather than
 * leaking a raw SCREAMING_CASE string into the UI.
 */

/** Drives which icon and colour the row gets. */
export type RewardTransactionTone = 'referral' | 'package' | 'redemption';

export interface RewardTransactionDisplay {
  label: string;
  tone: RewardTransactionTone;
}

const EXACT: Record<string, RewardTransactionDisplay> = {
  'PACKAGE SHIPPED': { label: 'Package Shipped', tone: 'package' },
  'FIRST PACKAGE SHIPPED BY REFEREE': { label: "Referral's First Shipment", tone: 'referral' },
  'EMAIL VERIFICATION REFERRER': { label: 'Email Verification Reward', tone: 'referral' },
  'EMAIL VERIFICATION REFERRED': { label: 'Referral Signup Bonus', tone: 'referral' },
  'PHONE VERIFICATION REFERRER': { label: 'Phone Verification Reward', tone: 'referral' },
  'PHONE VERIFICATION REFERRED': { label: 'Phone Verification Bonus', tone: 'referral' },
  'SIGNUP VERIFICATION REFERRER': { label: 'Referral Bonus', tone: 'referral' },
  'SIGNUP VERIFICATION REFERRED': { label: 'Welcome Bonus', tone: 'referral' },
  REDEEM: { label: 'Reward Redeemed', tone: 'redemption' },
  'POINTS REDEEMED': { label: 'Points Redeemed at Checkout', tone: 'redemption' },
  'CREDIT REDEEMED': { label: 'Credit Redeemed at Checkout', tone: 'redemption' },
  'CLAIM REFUNDED': { label: 'Prize Claim Refunded', tone: 'redemption' },
};

/**
 * Legacy types that end in a user id, so they can never match by equality.
 * Order matters: 'REFERRED BY n' (the referee's half) must be tested before
 * 'REFERRED n' (the referrer's full award), which is a prefix of it.
 */
const PREFIXES: Array<[string, RewardTransactionDisplay]> = [
  ['REFERRED BY', { label: 'Welcome Bonus', tone: 'referral' }],
  ['REFFERED USER', { label: 'Referral Bonus', tone: 'referral' }], // sic — typo is in the API
  ['REFERRED', { label: 'Referral Bonus', tone: 'referral' }],
];

const normalize = (type: string) => type.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toUpperCase();

const titleCase = (normalized: string) =>
  normalized
    .split(' ')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

export const getRewardTransactionDisplay = (
  type?: string | null
): RewardTransactionDisplay => {
  const normalized = type ? normalize(type) : '';
  if (!normalized) return { label: 'Reward', tone: 'redemption' };

  const exact = EXACT[normalized];
  if (exact) return exact;

  const prefixed = PREFIXES.find(([prefix]) => normalized.startsWith(prefix));
  if (prefixed) return prefixed[1];

  return { label: titleCase(normalized), tone: 'redemption' };
};
